import { Menu, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useBloquearScroll, useEsMobile } from "../../hooks";
import { cn } from "../../lib/cn";
import { BloqueDeUsuario } from "./BloqueDeUsuario";
import { Campanita } from "./Campanita";
import { EtiquetaEntorno } from "./entorno";
import { navegacionVisible } from "./navegacion";
import { PrintFlowMark } from "./PrintFlowMark";
import { Sidebar } from "./Sidebar";

/**
 * Layout de la aplicación, porteado de `AppLayout.tsx` de `imprenta`
 * (SPEC 0003, fase 1).
 *
 * **Dos layouts distintos, no uno responsive.** En escritorio el sidebar es una
 * columna fija; en mobile es un drawer que **empuja** el contenido a la derecha
 * en vez de taparlo. Ese patrón de empuje es de `imprenta` y se portea tal cual:
 * el contenido se desplaza 288px y el overlay viaja con él, así que oscurece
 * también la barra superior.
 *
 * Tres cosas que el drawer resuelve y que se pierden si se rehacen a ojo:
 * cerrarse al cambiar de ruta, cerrarse al pasar a escritorio, y bloquear el
 * scroll del body mientras está abierto.
 */
export function AppLayout({
  children,
  puede,
  nombre,
  email,
  roles,
  alSalir,
}: {
  children: ReactNode;
  puede: (codigo: string) => boolean;
  nombre: string;
  email: string;
  roles: readonly string[];
  alSalir: () => void;
}) {
  const esMobile = useEsMobile();
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const ubicacion = useLocation();

  // Al navegar, el drawer se cierra: si no, se queda abierto sobre la pantalla
  // nueva y hay que cerrarlo a mano cada vez.
  //
  // La ruta no se USA adentro, es el DISPARADOR — por eso Biome la ve como una
  // dependencia de más. Sacarla haría que el efecto corra una sola vez y el
  // drawer no se cierre nunca al navegar.
  // biome-ignore lint/correctness/useExhaustiveDependencies: la ruta dispara, no se lee
  useEffect(() => {
    setDrawerAbierto(false);
  }, [ubicacion.pathname]);

  // Al pasar a escritorio también, o queda un drawer invisible capturando
  // clicks.
  useEffect(() => {
    if (!esMobile) setDrawerAbierto(false);
  }, [esMobile]);

  useBloquearScroll(esMobile && drawerAbierto);

  const cerrar = useCallback(() => setDrawerAbierto(false), []);

  if (!esMobile) {
    return (
      /**
       * Shell FLOTANTE, tomado de `tagliapietra-portal` (ADR 0060).
       *
       * El `p-3` del contenedor y el `gap-3` son todo el mecanismo: la sidebar
       * deja de estar pegada al borde de la ventana y al contenido, y pasa a
       * ser una superficie apoyada sobre el fondo. Sin ese aire, el redondeo y
       * la sombra no se ven — es el espacio lo que hace la diferencia, no la
       * sombra.
       *
       * El fondo lleva un degradé apenas perceptible con el color de marca,
       * para que la superficie blanca tenga contra qué recortarse.
       */
      <div className="flex h-screen gap-3 overflow-hidden bg-fondo p-3">
        <Sidebar puede={puede} nombre={nombre} email={email} roles={roles} alSalir={alSalir} />
        {/* Sin `max-w-7xl` (ADR 0091). `imprenta` lo lleva, y acá se copió: con
            la sidebar afuera, en una pantalla ancha el contenido quedaba
            centrado en 1256 px dejando dos franjas vacías a los costados. Los
            listados tienen seis y siete columnas y son lo que más se mira, así
            que ese ancho es el que hace falta. */}
        <main className="flex-1 overflow-y-auto">
          <div className="px-3 py-2">{children}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative h-screen overflow-hidden bg-neutral-50">
      <SidebarDrawer puede={puede} nombre={nombre} email={email} roles={roles} alSalir={alSalir} />

      {/* El envoltorio se desplaza; el drawer queda fijo detrás. */}
      <div
        className={cn(
          "relative z-20 flex h-screen flex-col bg-neutral-50 transition-transform duration-300 ease-in-out",
          drawerAbierto ? "translate-x-72" : "translate-x-0",
        )}
      >
        <MobileNavbar abierto={drawerAbierto} alAlternar={() => setDrawerAbierto((v) => !v)} />

        {/* El overlay va DENTRO del envoltorio para que viaje con él y oscurezca
            también la barra superior. */}
        <button
          type="button"
          tabIndex={drawerAbierto ? 0 : -1}
          aria-label="Cerrar menú"
          onClick={cerrar}
          className={cn(
            "fixed inset-0 z-50 bg-black/50 transition-opacity duration-300",
            drawerAbierto ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        />

        <main className="flex-1 overflow-y-auto pt-14">
          <div className="mx-auto max-w-7xl p-4">{children}</div>
        </main>
      </div>
    </div>
  );
}

/** Barra superior de mobile. Fija, con el botón de menú. */
function MobileNavbar({ abierto, alAlternar }: { abierto: boolean; alAlternar: () => void }) {
  return (
    <header className="fixed top-0 right-0 left-0 z-40 flex h-14 items-center gap-3 border-neutral-200 border-b bg-neutral-50 px-4">
      <button
        type="button"
        onClick={alAlternar}
        aria-label={abierto ? "Cerrar menú" : "Abrir menú"}
        aria-expanded={abierto}
        className="text-neutral-700"
      >
        {abierto ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 ring-1 ring-brand-100">
        <PrintFlowMark className="h-6 w-6" />
      </span>
      <span className="font-extrabold text-neutral-900 text-lg tracking-tight">PrintFlow</span>
      <EtiquetaEntorno />
      {/* A la derecha, que es donde `imprenta` puso su placeholder. */}
      <div className="ml-auto">
        <Campanita />
      </div>
    </header>
  );
}

/**
 * El panel del drawer. Queda **siempre montado** detrás del contenido, no se
 * monta al abrir: así la transición de empuje es continua y no aparece de golpe.
 */
function SidebarDrawer({
  puede,
  nombre,
  email,
  roles,
  alSalir,
}: {
  puede: (codigo: string) => boolean;
  nombre: string;
  email: string;
  roles: readonly string[];
  alSalir: () => void;
}) {
  const grupos = navegacionVisible(puede);

  // El drawer acompaña a la sidebar de escritorio (ADR 0061): es el MISMO menú,
  // y verlo claro en el teléfono y oscuro en la computadora lo haría parecer
  // otra cosa. Acá va pegado al borde y sin redondeo, porque ocupa la pantalla
  // entera — flotar no tiene sentido cuando no hay nada alrededor.
  return (
    <aside className="fixed inset-y-0 left-0 z-10 flex w-72 flex-col bg-gradient-to-b from-sb to-sb-2">
      <div className="flex items-center gap-3 border-sb-border border-b p-6">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 shadow-sm ring-1 ring-white/10">
          <PrintFlowMark className="h-9 w-9" />
        </span>
        <div className="flex flex-col">
          <span className="font-extrabold text-sb-text text-xl tracking-tight">PrintFlow</span>
          <EtiquetaEntorno />
        </div>
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-6">
        {grupos.map((grupo) => (
          <div key={grupo.titulo ?? "principal"} className="mb-4">
            {grupo.titulo ? (
              <p className="mb-2 px-3 font-semibold text-sb-muted text-xs uppercase tracking-wider">
                {grupo.titulo}
              </p>
            ) : null}
            {grupo.items.map((item) => {
              const Icono = item.icono;
              return (
                <NavLink
                  key={item.ruta}
                  to={item.ruta}
                  className={({ isActive }) =>
                    cn(
                      "mb-0.5 flex items-center gap-3 rounded-md px-3 py-2 font-medium text-sm",
                      isActive ? "bg-white/10 text-white" : "text-sb-text hover:bg-white/5",
                    )
                  }
                >
                  <Icono className="h-5 w-5 shrink-0" />
                  {item.texto}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* El MISMO bloque que el sidebar de escritorio: quién sos y el menú de
          la cuenta. Dos pies distintos para lo mismo divergen. */}
      <div className="border-sb-border border-t p-3">
        <BloqueDeUsuario
          nombre={nombre}
          email={email}
          roles={roles}
          colapsado={false}
          alSalir={alSalir}
        />
      </div>
    </aside>
  );
}
